
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'
import * as core from '../core'
import * as entity from '../entity'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line

/**
 * TokensDeleteParams struct
 */
class TokensDeleteParams {
  /**
   * Initialize struct
   * @param {!Array=} pairId
   * @constructor
   */
  constructor (pairId = []) {
    this.PairId = pairId
  }

  /**
   * Copy struct (shallow copy)
   * @this {!TokensDeleteParams}
   * @param {!TokensDeleteParams} other Other struct
   * @returns {!TokensDeleteParams} This struct
   */
  copy (other) {
    if (other.PairId != null) {
      this.PairId = []
      for (let item of other.PairId) {
        if (item != null) {
          let tempItem
          tempItem = UInt64.fromNumber(item)
          this.PairId.push(tempItem)
        } else {
          this.PairId.push(null)
        }
      }
    } else {
      this.PairId = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!TokensDeleteParams}
   * @returns {!TokensDeleteParams} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new TokensDeleteParamsModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new TokensDeleteParamsModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!TokensDeleteParams}
   * @param {!TokensDeleteParams} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof TokensDeleteParams)) {
      throw new TypeError('Instance of TokensDeleteParams is required!')
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!TokensDeleteParams}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    return {
      PairId: ((this.PairId != null) ? Array.from(this.PairId, item => ((item != null) ? item.toNumber() : null)) : null)
    }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return TokensDeleteParams.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!TokensDeleteParams} other Object value
   * @returns {!TokensDeleteParams} Created struct
   */
  static fromObject (other) {
    return new TokensDeleteParams().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!TokensDeleteParams}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return TokensDeleteParams.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!TokensDeleteParams}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 14121
  }
}

export { TokensDeleteParams };

/**
 * Fast Binary Encoding TokensDeleteParams field model
 */
class FieldModelTokensDeleteParams extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this.PairId = new fbe.FieldModelVector(new fbe.FieldModelUInt64(buffer, 4 + 4), buffer, 4 + 4)
  }

  /**
   * Get the field size
   * @this {!FieldModelTokensDeleteParams}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelTokensDeleteParams}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.PairId.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelTokensDeleteParams}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.PairId.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelTokensDeleteParams}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelTokensDeleteParams.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelTokensDeleteParams}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 14121
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelTokensDeleteParams}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelTokensDeleteParams}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.PairId.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.PairId.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.PairId.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelTokensDeleteParams}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelTokensDeleteParams}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelTokensDeleteParams}
   * @param {!TokensDeleteParams} fbeValue Default value, defaults is new TokensDeleteParams()
   * @returns {!TokensDeleteParams} TokensDeleteParams value
   */
  get (fbeValue = new TokensDeleteParams()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelTokensDeleteParams}
   * @param {!TokensDeleteParams} fbeValue TokensDeleteParams value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.PairId.fbeSize) <= fbeStructSize) {
      this.PairId.get(fbeValue.PairId)
    } else {
      fbeValue.PairId.length = 0
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.PairId.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelTokensDeleteParams}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelTokensDeleteParams}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelTokensDeleteParams}
   * @param {!TokensDeleteParams} fbeValue TokensDeleteParams value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelTokensDeleteParams}
   * @param {TokensDeleteParams} fbeValue TokensDeleteParams value
   */
  setFields (fbeValue) {
    this.PairId.set(fbeValue.PairId)
  }
}

export { FieldModelTokensDeleteParams };

/**
 * Fast Binary Encoding TokensDeleteParams model
 */
class TokensDeleteParamsModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelTokensDeleteParams(this.buffer, 4)
  }

  /**
   * Get the TokensDeleteParams model
   * @this {!TokensDeleteParamsModel}
   * @returns {!FieldModelTokensDeleteParams} model TokensDeleteParams model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!TokensDeleteParamsModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!TokensDeleteParamsModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return TokensDeleteParamsModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!TokensDeleteParamsModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelTokensDeleteParams.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!TokensDeleteParamsModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!TokensDeleteParamsModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!TokensDeleteParamsModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!TokensDeleteParamsModel}
   * @param {!TokensDeleteParams} value TokensDeleteParams value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!TokensDeleteParamsModel}
   * @param {!TokensDeleteParams} value TokensDeleteParams value, defaults is new TokensDeleteParams()
   * @return {!object} Deserialized TokensDeleteParams value and its size
   */
  deserialize (value = new TokensDeleteParams()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new TokensDeleteParams(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new TokensDeleteParams(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!TokensDeleteParamsModel}
   * @param {!number} prev Previous TokensDeleteParams model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { TokensDeleteParamsModel };
